import { useState, useEffect, useMemo, type ChangeEvent, type KeyboardEvent, type FC } from 'react';
import { MUIModal } from '@/app/shared/ui';
import { useAppDispatch } from '@/store/hooks';
import { updateReference } from '@/store/reducers/uploadSlice';
import type { State, RelationshipType, EmergencyContact } from './references.services';

const initialFieldValues: Partial<EmergencyContact> = {
  fullName: '',
  phoneNumber: '',
  emailAddress: '',
  streetLine1: '',
  city: '',
  zip: '',
  relationshipTypeId: undefined,
  state: '',
};

interface TransformedState {
  id: string;
  value: string;
  stateName: string;
}

interface TransformedRelationship {
  id: number;
  value: string;
}

interface ReferenceCardProps {
  showModal: boolean;
  handleClose: () => void;
  referenceData?: Partial<EmergencyContact>;
  referenceHeading: string;
  stateList: State[];
  relationshipList: RelationshipType[];
  contactId?: string;
}

const ReferenceCard: FC<ReferenceCardProps> = ({
  showModal,
  handleClose,
  referenceData,
  referenceHeading,
  stateList,
  relationshipList,
}) => {
  const dispatch = useAppDispatch();
  const [inputFields, setInputFields] = useState<Partial<EmergencyContact>>(initialFieldValues);

  const phoneNumberRegex = /^[2-9]\d{0,9}$/;
  const zipCodeRegex = /^\d{0,5}(-\d{0,3})?$/;

  const modifyStateList = (list: State[]): TransformedState[] => {
    if (!Array.isArray(list)) return [];
    return list.map((rec) => ({
      id: rec.stateId,
      value: rec.stateName,
      stateName: rec.stateName,
    }));
  };

  const modifyRelationShipList = (list: RelationshipType[]): TransformedRelationship[] => {
    if (!Array.isArray(list)) return [];
    return list.map((rec) => ({
      id: rec.relationshipTypeId,
      value: rec.relationShipName,
    }));
  };

  const localStateList = useMemo(() => modifyStateList(stateList), [stateList]);
  const localRelationshipList = useMemo(() => modifyRelationShipList(relationshipList), [relationshipList]);

  useEffect(() => {
    if (showModal) {
      const timer = setTimeout(() => {
        setInputFields(referenceData || initialFieldValues);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [showModal, referenceData]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    let cleanValue: string | number = name === 'phoneNumber' || name === 'zip' ? value.replace(/\D/g, '') : value;

    if (name === 'relationshipTypeId') {
      cleanValue = parseInt(value, 10);
    }

    setInputFields((prev) => ({
      ...prev,
      [name]: cleanValue,
    }));
  };

  const smartDisable = (): boolean => {
    const { fullName, phoneNumber, emailAddress, relationshipTypeId, zip } = inputFields;
    if (fullName && phoneNumber && emailAddress && relationshipTypeId) {
      const formattedZip = zip || '';
      const mailformat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      return !(emailAddress.match(mailformat) && phoneNumber.match(phoneNumberRegex) && formattedZip.match(zipCodeRegex));
    }
    return true;
  };

  const getStateCode = (name: string): string | undefined => {
    const match = localStateList.find((s) => s.id === name || s.value === name);
    return match ? match.value : undefined;
  };

  const getStateCodeModal = (name: string): string | undefined => {
    const match = localStateList.find((s) => s.value === name);
    return match ? match.stateName : undefined;
  };

  const getRelationShipIdModal = (name: number): string | undefined => {
    const match = localRelationshipList.find((r) => r.id === name);
    return match ? match.value : undefined;
  };

  const handleSave = () => {
    const { fullName, phoneNumber, emailAddress, streetLine1, city, zip, state, relationshipTypeId } = inputFields;

    const payload = {
      fullName,
      phoneNumber,
      emailAddress,
      relationshipTypeId,
      streetLine1,
      city,
      state: getStateCode(state || ''),
      zip,
    };

    dispatch(
      updateReference({
        key: referenceHeading,
        reference: payload,
      })
    );
    handleClose();
  };

  const onClose = () => {
    handleClose();
  };

  const phoneNumberValidation = (e: KeyboardEvent<HTMLInputElement>) => {
    const currentValue = (e.target as HTMLInputElement).value + e.key;
    if (!phoneNumberRegex.test(currentValue)) {
      e.preventDefault();
    }
  };

  const zipCodeValidation = (e: KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const key = e.key;
    let currentValue = input.value;

    if (!/\d/.test(key)) {
      e.preventDefault();
      return;
    }

    if (currentValue.length === 5 && !currentValue.includes('-')) {
      currentValue = currentValue.slice(0, 5) + '-' + currentValue.slice(5);
    }

    if (!zipCodeRegex.test(currentValue)) {
      e.preventDefault();
    } else {
      input.value = currentValue;
    }
  };

  return (
    <MUIModal
      open={showModal}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div className="modal-size">
        <div className="w3-modal-content">
          <div className="w3-container">
            <span
              className="w3-button w3-display-topright"
              onClick={onClose}
            >
              &times;
            </span>
            <form>
              <div className="addReferenceContainer">
                <div className="input-group">
                  <input
                    className="textbox"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={inputFields.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="input-group">
                  <input
                    className="textbox"
                    type="text"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={inputFields.phoneNumber}
                    onChange={handleChange}
                    onKeyPress={phoneNumberValidation}
                    required
                  />
                </div>
                <div className="input-group">
                  <input
                    className="textbox"
                    type="email"
                    name="emailAddress"
                    placeholder="Email Address"
                    value={inputFields.emailAddress}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="input-group">
                  <select
                    className="dropdown"
                    name="relationshipTypeId"
                    onChange={handleChange}
                    value={inputFields.relationshipTypeId}
                    required
                  >
                    <option
                      value=""
                      disabled
                      hidden
                    >
                      {referenceData && referenceData.relationshipTypeId
                        ? getRelationShipIdModal(referenceData.relationshipTypeId)
                        : 'Relationship'}
                    </option>
                    {localRelationshipList.map((relationship) => (
                      <option
                        key={relationship.id}
                        value={relationship.id}
                      >
                        {relationship.value}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <input
                    className="textbox"
                    type="text"
                    name="streetLine1"
                    placeholder="Address"
                    value={inputFields.streetLine1}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <input
                    className="textbox"
                    type="text"
                    name="city"
                    placeholder="City"
                    value={inputFields.city}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <select
                    className="dropdown"
                    name="state"
                    onChange={handleChange}
                    value={inputFields.state}
                  >
                    <option
                      value=""
                      disabled
                      hidden
                    >
                      {referenceData && referenceData.state ? getStateCodeModal(referenceData.state) : 'State'}
                    </option>
                    {localStateList.map((state) => (
                      <option
                        key={state.id}
                        value={state.value}
                      >
                        {state.stateName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <input
                    className="textbox"
                    type="text"
                    name="zip"
                    placeholder="Zip"
                    value={inputFields.zip}
                    onChange={handleChange}
                    onKeyPress={zipCodeValidation}
                  />
                </div>
                <div>
                  <input
                    className="cancel"
                    type="button"
                    value="CANCEL"
                    onClick={onClose}
                  />
                </div>
                <div>
                  <input
                    className="save"
                    type="button"
                    value="SAVE"
                    onClick={handleSave}
                    disabled={smartDisable()}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MUIModal>
  );
};

export default ReferenceCard;
